import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Col, Flex, Form, Input, Row, Select } from 'antd';
import FadeIn from '../FadeIn';
import useFeedbackLabel from '../../hooks/ui/useFeedbackLabel';
import useForm from '../../hooks/utility/useForm';
import useBusinessProfile from '../../hooks/context/useBusinessProfile';
import AddressFields from '../form/address/AddressFields';

export default function ShippingInfoStep(props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Verification:ShippingInfoStep' });
  const { moveBackwards, moveForward, buttonText } = props;

  const { getValue } = useBusinessProfile();
  const { updating, pushUpdates } = useBusinessProfile();
  const { setErrorFeedback, renderFeedbackLabel } = useFeedbackLabel({ t });

  const { data, update } = useForm({
    shipping_company_name: getValue('shipping_company_name') ?? getValue('display_name'),
    shipping_phone_number: getValue('shipping_phone_number') ?? getValue('owner_phone_number'),
    shipping_salutation: getValue('shipping_salutation') ?? t('form.shipping_salutation.options.mr'),
    shipping_first_name: getValue('shipping_first_name') ?? getValue('owner_first_name'),
    shipping_last_name: getValue('shipping_last_name') ?? getValue('owner_last_name'),
    shipping_address: getValue('shipping_address') ?? getValue('legal_address'),
    shipping_city: getValue('shipping_city') ?? getValue('legal_city'),
    shipping_state: getValue('shipping_state') ?? getValue('legal_state'),
    shipping_country: getValue('shipping_country') ?? getValue('legal_country'),
    shipping_zip_code: getValue('shipping_zip_code') ?? getValue('legal_zip_code'),
  });

  const save = () =>
    pushUpdates({
      data,
      onSuccess: () => moveForward && moveForward(),
      onError: ({ data }) => setErrorFeedback(data),
    });

  return (
    <FadeIn>
      {renderFeedbackLabel}
      <Form layout="vertical">
        <Form.Item label={t('form.shipping_company_name.label')} required>
          <Input
            placeholder={t('form.shipping_company_name.placeholder')}
            value={data.shipping_company_name ?? ''}
            onChange={(e) => update('shipping_company_name', e.target.value)}
          />
        </Form.Item>
        <Form.Item label={t('form.shipping_phone_number.label')} required>
          <Input
            placeholder={t('form.shipping_phone_number.placeholder')}
            value={data.shipping_phone_number ?? ''}
            onChange={(e) => update('shipping_phone_number', e.target.value)}
          />
        </Form.Item>
        <Form.Item label={t('form.shipping_salutation.label')} required>
          <Select
            placeholder={t('form.shipping_salutation.placeholder')}
            value={data.shipping_salutation ?? ''}
            onChange={(value) => update('shipping_salutation', value)}
            options={[
              { label: t('form.shipping_salutation.options.mr'), value: t('form.shipping_salutation.options.mr') },
              { label: t('form.shipping_salutation.options.mrs'), value: t('form.shipping_salutation.options.mrs') },
            ]}
          />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label={t('form.shipping_first_name.label')} required>
              <Input
                placeholder={t('form.shipping_first_name.placeholder')}
                value={data.shipping_first_name ?? ''}
                onChange={(e) => update('shipping_first_name', e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('form.shipping_last_name.label')} required>
              <Input
                placeholder={t('form.shipping_last_name.placeholder')}
                value={data.shipping_last_name ?? ''}
                onChange={(e) => update('shipping_last_name', e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
        <AddressFields prefix="shipping" useGrid data={data} onChange={update} />
      </Form>
      <Flex justify="end">
        {moveBackwards && (
          <Button onClick={moveBackwards} className="mr-md">
            {t('buttons.back')}
          </Button>
        )}
        <Button type="primary" onClick={save} loading={updating}>
          {buttonText ?? t('buttons.next')}
        </Button>
      </Flex>
    </FadeIn>
  );
}
